import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Stack,
  TextField,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import {
  copiarPrecosEstabelecimento,
  getConfiguracao,
  getEstabelecimentos,
} from '../../services/api';
import { getApiErrorMessage } from '../../utils/apiError';
import PageHeader from '../common/PageHeader';

export default function CopiarPrecosFilialPage() {
  const [filiais, setFiliais] = useState([]);
  const [origemId, setOrigemId] = useState('');
  const [destinoId, setDestinoId] = useState('');
  const [sobrescrever, setSobrescrever] = useState(true);
  const [precoPorFilial, setPrecoPorFilial] = useState(true);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [okMsg, setOkMsg] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const estabRes = await getEstabelecimentos();
      const lista = Array.isArray(estabRes.data) ? estabRes.data : [];
      let politicaPorFilial = true;
      if (lista.length > 0) {
        try {
          const cfgRes = await getConfiguracao(lista[0].id);
          politicaPorFilial = Boolean(cfgRes.data?.precoPorFilial);
        } catch {
          politicaPorFilial = true;
        }
      }
      setFiliais(lista);
      setPrecoPorFilial(politicaPorFilial);
      if (lista.length >= 2) {
        setOrigemId(String(lista[0].id));
        setDestinoId(String(lista[1].id));
      } else if (lista.length === 1) {
        setOrigemId(String(lista[0].id));
      }
    } catch (e) {
      setError(getApiErrorMessage(e, 'Não foi possível carregar as filiais.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const opcoesDestino = useMemo(
    () => filiais.filter((f) => String(f.id) !== origemId),
    [filiais, origemId],
  );

  useEffect(() => {
    if (!destinoId || destinoId === origemId) {
      const prox = opcoesDestino[0];
      if (prox) setDestinoId(String(prox.id));
    }
  }, [origemId, destinoId, opcoesDestino]);

  const copiar = async () => {
    setError('');
    setOkMsg('');
    if (!origemId || !destinoId) {
      setError('Selecione filial de origem e destino.');
      return;
    }
    if (origemId === destinoId) {
      setError('Origem e destino devem ser filiais diferentes.');
      return;
    }
    const origem = filiais.find((f) => String(f.id) === origemId);
    const destino = filiais.find((f) => String(f.id) === destinoId);
    const msg =
      `Copiar todos os preços de "${origem?.nomeFantasia || origem?.razaoSocial || origemId}" ` +
      `para "${destino?.nomeFantasia || destino?.razaoSocial || destinoId}"?`;
    if (!window.confirm(msg)) return;

    setSubmitting(true);
    try {
      const { data } = await copiarPrecosEstabelecimento({
        estabelecimentoOrigemId: Number(origemId),
        estabelecimentoDestinoId: Number(destinoId),
        sobrescreverExistentes: sobrescrever,
      });
      setOkMsg(
        `Concluído: ${data?.totalOrigem ?? 0} preço(s) na origem — ` +
          `${data?.criados ?? 0} criado(s), ${data?.atualizados ?? 0} atualizado(s), ` +
          `${data?.ignorados ?? 0} ignorado(s).`,
      );
    } catch (e) {
      setError(getApiErrorMessage(e, 'Não foi possível copiar os preços.'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <PageHeader
        title="Copiar preços entre filiais"
        subtitle="Replica varejo, atacado, promocional e custo da filial origem para a destino (todas as tabelas de preço)."
      />

      {loading && <LinearProgress sx={{ mb: 2 }} />}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {okMsg && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setOkMsg('')}>
          {okMsg}
        </Alert>
      )}
      {!precoPorFilial && filiais.length > 1 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          A política atual é <strong>preço global</strong> (ao salvar um produto os preços já
          replicam em todas as filiais). Use esta ferramenta para alinhar uma filial com outra
          ou após ativar preço por filial.
        </Alert>
      )}

      <Paper variant="outlined" sx={{ p: 3, maxWidth: 520 }}>
        <Stack spacing={2}>
          <TextField
            select
            label="Filial origem"
            value={origemId}
            onChange={(e) => setOrigemId(e.target.value)}
            fullWidth
            disabled={submitting || filiais.length < 2}
          >
            {filiais.map((f) => (
              <MenuItem key={f.id} value={String(f.id)}>
                {f.nomeFantasia || f.razaoSocial || `Filial ${f.id}`}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            label="Filial destino"
            value={destinoId}
            onChange={(e) => setDestinoId(e.target.value)}
            fullWidth
            disabled={submitting || opcoesDestino.length === 0}
          >
            {opcoesDestino.map((f) => (
              <MenuItem key={f.id} value={String(f.id)}>
                {f.nomeFantasia || f.razaoSocial || `Filial ${f.id}`}
              </MenuItem>
            ))}
          </TextField>

          <FormControlLabel
            control={
              <Checkbox
                checked={sobrescrever}
                onChange={(e) => setSobrescrever(e.target.checked)}
                disabled={submitting}
              />
            }
            label="Sobrescrever preços já existentes na filial destino"
          />

          <Button
            variant="contained"
            startIcon={<ContentCopyIcon />}
            onClick={copiar}
            disabled={submitting || filiais.length < 2 || !origemId || !destinoId}
          >
            {submitting ? 'Copiando…' : 'Copiar preços'}
          </Button>
        </Stack>
      </Paper>
    </Box>
  );
}
